// Single source of truth for which permission(s) gate each Application Management area —
// used by the sidebar nav filter, the route guard, and the tab list inside AppManagement.
//
// Application Management administers the Weightrax platform itself (tenants, shards, menu/page
// definitions, roles, monitoring) rather than the selected tenant, so the whole section is gated
// on the platform super-user permission first: a tab is visible/reachable only if the user holds
// 'manage:application' AND one of the tab's own permission codes. In practice 'manage:application'
// alone is enough, since usePermissions().hasPermission treats it as a blanket bypass — the per-tab
// lists stay as the record of what each area actually administers, and would start to bite if a
// tab's permissions were ever granted to a role that also holds 'manage:application'.
//
// The section gate is what stops a tenant-scoped permission that happens to appear on one tab's
// list from unlocking the section: 'manage:users' is held by Business Admin, Site Manager, Site
// Administrator and User Admin, and before the gate that was enough to put App Management in the
// sidebar and open its Users tab. Tenant-scoped user administration lives on Admin → Users
// (see utils/adminAccess.ts).

export const APP_MANAGEMENT_ROUTE = '/application-management';

// Platform super-user permission (Permissions.ManageApplication on the API).
export const APP_MANAGEMENT_PERMISSION = 'manage:application';

export interface AppManagementTabAccess {
  label: string;
  param: string;
  permissions: string[];
}

export const APP_MANAGEMENT_TABS: AppManagementTabAccess[] = [
  { label: 'Clients / Tenants', param: 'tenants', permissions: ['manage:tenants', 'read:tenants'] },
  { label: 'Databases', param: 'databases', permissions: ['manage:shards', 'read:shards'] },
  { label: 'Menu Items', param: 'menu-items', permissions: ['manage:application'] },
  { label: 'Peripherals', param: 'peripherals', permissions: ['manage:peripherals', 'read:peripherals'] },
  { label: 'Page Definitions', param: 'page-definitions', permissions: ['manage:application'] },
  { label: 'Roles & Permissions', param: 'roles', permissions: ['manage:roles', 'read:roles'] },
  // Platform-operator view of the same tenant-scoped page that tenant roles reach via
  // Admin → Users; reachable here only because 'manage:application' passes the section gate.
  { label: 'Users', param: 'users', permissions: ['manage:users', 'read:users'] },
  { label: 'Settings', param: 'settings', permissions: ['manage:application'] },
  // Platform-wide list prices: every module a tenant can license, and what it costs over time.
  { label: 'Modules & Pricing', param: 'modules', permissions: ['manage:application'] },
  // The standard reports defined for the whole application: their module, permission and definition.
  { label: 'Report Definitions', param: 'reports', permissions: ['manage:application'] },
  // Which of those reports each tenant sees on its Reports page (shard TenantReport).
  { label: 'Tenant Reports', param: 'tenant-reports', permissions: ['manage:application'] },
  //{ label: 'Monitoring', permissions: ['manage:application'] },
  //{ label: 'Alerts', permissions: ['manage:application'] },
  //{ label: 'Integrations', permissions: ['manage:application'] },
  // Platform-operator repair for shard rows that wedge a device pull; see views/app-management/SyncHealthManagement.
  { label: 'Sync Monitoring', param: 'sync-monitoring', permissions: ['manage:application'] },
  { label: 'Sync Repair', param: 'sync-repair', permissions: ['manage:application'] }
];

export const TenantTab = 0;
export const ShardTab = 1;
export const MenuItemsTab = 2;
export const PeripheralsTab = 3;
export const PageViewsTab = 4;
export const RolesTab = 5;
export const UsersTab = 6;
export const SettingsTab = 7;
export const ModulesTab = 8;
export const ReportsTab = 9;
export const TenantReportsTab = 10;
export const SyncMonitoringTab = 11;
export const SyncHealthTab = 12;

export function canAccessAppManagementTab(hasPermission: (permissionCode: string) => boolean, tabIndex: number): boolean {
  const tab = APP_MANAGEMENT_TABS[tabIndex];
  if (!tab) return false;
  if (!hasAnyAppManagementAccess(hasPermission)) return false;
  return tab.permissions.some((code) => hasPermission(code));
}

export function hasAnyAppManagementAccess(hasPermission: (permissionCode: string) => boolean): boolean {
  return hasPermission(APP_MANAGEMENT_PERMISSION);
}
